import type { Locale } from './types';

/** ISO datetime string in local time with offset, e.g. 2026-08-19T03:12:45-06:00 */
export function nowIso(): string {
  return toLocalIso(new Date());
}

export function toLocalIso(d: Date): string {
  const pad = (n: number, w = 2) => String(Math.trunc(Math.abs(n))).padStart(w, '0');
  const offMin = -d.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(offMin / 60)}:${pad(offMin % 60)}`
  );
}

/** Local calendar date (yyyy-mm-dd) for an ISO datetime, in this device's zone. */
export function localDateOf(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayLocalDate(): string {
  return localDateOf(nowIso());
}

/**
 * Baby's day of life. Day 1 = the calendar day of birth (clinical convention
 * used in the handbook's day-by-day table). Returns null before birth.
 */
export function dayOfLife(birthAt: string | undefined, now: Date = new Date()): number | null {
  if (!birthAt) return null;
  const birth = new Date(birthAt);
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(birth)) / 86_400_000);
  return days + 1;
}

/** Whole days until an ISO date (yyyy-mm-dd), negative if past. */
export function daysUntil(isoDate: string, now: Date = new Date()): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  const target = new Date(y, m - 1, d).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((target - today) / 86_400_000);
}

export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

export function formatTime(iso: string, locale: Locale): string {
  return new Date(iso).toLocaleTimeString(locale === 'es' ? 'es-US' : 'en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDate(isoDateOrDatetime: string, locale: Locale): string {
  const d = isoDateOrDatetime.includes('T')
    ? new Date(isoDateOrDatetime)
    : (() => {
        const [y, m, dd] = isoDateOrDatetime.split('-').map(Number);
        return new Date(y, m - 1, dd);
      })();
  return d.toLocaleDateString(locale === 'es' ? 'es-US' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Minutes elapsed since an ISO datetime. */
export function minutesSince(iso: string, now: Date = new Date()): number {
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
}

/** "2h 15m ago" style elapsed label parts; render via i18n. */
export function elapsedParts(iso: string, now: Date = new Date()): { hours: number; minutes: number } {
  const mins = Math.max(0, minutesSince(iso, now));
  return { hours: Math.floor(mins / 60), minutes: mins % 60 };
}
